<template>
  <div :class="{ 'phone-mode': isPhoneMode }">
    <IntercomInput
      :model-value="modelValue"
      type="text"
      :inputmode="isPhoneMode ? 'numeric' : 'email'"
      :maxlength="isPhoneMode ? 10 : undefined"
      :valid="valid"
      :error="error"
      :label="label"
      :placeholder="placeholder || 'Enter phone or email'"
      :required="required"
      :disabled="disabled"
      :hint="phoneHint"
      v-bind="$attrs"
      @update:model-value="handleInput"
      @blur="handleBlur"
    >
    <template #prefix>
      <Transition name="prefix-fade" mode="out-in">
        <span v-if="isPhoneMode" key="phone" class="phone-prefix">
          <span class="phone-code">+91</span>
          <span class="phone-divider"></span>
        </span>
        <svg
          v-else-if="isEmailMode"
          key="email"
          class="email-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="4"></circle>
          <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"></path>
        </svg>
      </Transition>
    </template>
  </IntercomInput>
  </div>
</template>

<script setup>
/**
 * IntercomPhoneOrEmailInput - Smart phone/email input with dynamic prefix
 *
 * Behavior:
 * - When first character is a digit → shows +91 prefix (phone mode)
 * - When first character is a letter or empty → shows @ icon (email mode)
 * - Smooth animated transition between modes
 *
 * This is for login/authentication fields where users can enter either format.
 * The backend normalizes phone numbers automatically, so we just show a visual hint.
 */
import { computed, watch } from 'vue'
import IntercomInput from './IntercomInput.vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  valid: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'blur', 'update:valid'])

// Detect if input looks like a phone number (starts with digit)
const isPhoneMode = computed(() => {
  if (!props.modelValue) return false
  return /^[0-9]/.test(props.modelValue)
})

// Detect if input looks like an email (starts with letter)
const isEmailMode = computed(() => {
  if (!props.modelValue) return false // Empty = no prefix
  return /^[a-zA-Z]/.test(props.modelValue)
})

// Check validity for phone or email
const isValidInput = computed(() => {
  const value = props.modelValue
  if (!value) return false

  // Phone: exactly 10 digits
  const isPhone = /^\d{10}$/.test(value)
  // Email: basic email pattern
  const isEmail = /^[\w\-\.]+@([\w\-]+\.)+[\w\-]{2,4}$/.test(value)

  return isPhone || isEmail
})

// Progress hint for phone mode (matches IntercomPhoneInput behavior)
const phoneHint = computed(() => {
  if (!isPhoneMode.value) return ''
  const len = props.modelValue.length
  if (len === 0) return ''
  if (len < 10) return `${len}/10`
  return 'Valid' // len === 10 (can't exceed due to input limiting)
})

// Emit validity changes
watch(
  isValidInput,
  (valid) => {
    emit('update:valid', valid)
  },
  { immediate: true }
)

const handleInput = (value) => {
  // Check if this looks like phone input (starts with digit)
  const looksLikePhone = value && /^[0-9]/.test(value)

  if (looksLikePhone) {
    // Strip non-digits and limit to 10
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
    emit('update:modelValue', digitsOnly)
    return
  }
  emit('update:modelValue', value)
}

const handleBlur = (event) => {
  emit('blur', event)
}
</script>

<style scoped>
.phone-prefix {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* Override input padding when phone prefix is shown - needs more space than default */
.phone-mode :deep(.intercom-input--prefix) {
  padding-left: 52px;
}

.phone-code {
  color: var(--color-gray-500);
  font-weight: 500;
  font-size: var(--font-size-base);
}

.phone-divider {
  width: 1px;
  height: 16px;
  margin-right: var(--space-2);
  background-color: var(--color-gray-300);
}

.email-icon {
  color: var(--color-gray-500);
}

/* Transition styles */
.prefix-fade-enter-active,
.prefix-fade-leave-active {
  transition: opacity 0.2s ease;
}

.prefix-fade-enter-from,
.prefix-fade-leave-to {
  opacity: 0;
}
</style>
